import { Component, inject, OnInit, HostListener, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Project, ProjectService } from '../../services/project/project.service';
import { Title, Meta } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-single-project-page',
  templateUrl: './single-project-page.component.html',
  styleUrl: './single-project-page.component.scss'
})
export class SingleProjectPageComponent implements OnInit, OnDestroy {
  private readonly siteUrl = environment.SITE_URL;
  project: Project | null = null;
  selectedImageIndex: number = 0;
  isImageModalOpen: boolean = false;
  maxVisibleThumbnails: number = 4;
  similarProjects: Project[] = [];
  
  // Zoom functionality
  zoomLevel: number = 1;
  minZoom: number = 1;
  maxZoom: number = 3;
  zoomStep: number = 0.25;
  isDragging: boolean = false;
  startX: number = 0;
  startY: number = 0;
  translateX: number = 0;
  translateY: number = 0;

  // Touch support
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchStartDistance: number = 0;
  private initialZoom: number = 1;

  private router = inject(Router);
  private projectService = inject(ProjectService);

  projects: Project[] = [];

  constructor(
    private route: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    this.projectService.getProjects().subscribe((data) => {
      this.projects = data;
      this.route.params.subscribe(params => {
        const projectId = +params['id'];
        const projectName = params['name'];
        this.loadProject(projectId, projectName);
      });
    });
  }

  ngOnDestroy(): void {
    // Clean up to prevent memory leaks
    if (isPlatformBrowser(this.platformId)) {
      this.closeImageModal();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.isImageModalOpen) return;

    switch (event.key) {
      case 'ArrowRight':
        this.nextImage();
        event.preventDefault();
        break;
      case 'ArrowLeft':
        this.previousImage();
        event.preventDefault();
        break;
      case 'Escape':
        this.closeImageModal();
        event.preventDefault();
        break;
      case '+':
      case '=':
        this.zoomIn();
        event.preventDefault();
        break;
      case '-':
      case '_':
        this.zoomOut();
        event.preventDefault();
        break;
    }
  }

  @HostListener('wheel', ['$event'])
  onMouseWheel(event: WheelEvent): void {
    if (!this.isImageModalOpen) return;
    
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  loadProject(projectId: number, projectName: string): void {
    const foundProject = this.projects
      .find(p =>
        p.id === projectId &&
        this.formatProjectName(p.title) === this.formatProjectName(projectName)
      );

    this.project = foundProject || null;
    if (this.project) {
      this.getSimilarProjects();
      this.setMetaTags();
      return;
    }

    this.router.navigate(['/proyectos']);
  }

  getSimilarProjects(): void {
    if (!this.project) return;

    this.similarProjects = this.projects
      .filter(p =>
        p.businessArea === this.project!.businessArea &&
        p.id !== this.project!.id
      )
      .slice(0, 3);
  }

  navigateToProject(projectId: number, projectName: string): void {

    const formattedName = this.formatProjectName(projectName);
    this.router.navigate(['/proyectos', formattedName, projectId]);

  }

  selectImage(index: number): void {
    this.selectedImageIndex = index;
  }

  openImageModal(index: number): void {
    this.selectedImageIndex = index;
    this.isImageModalOpen = true;
    this.resetZoom();
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden'; 
    }
  }

  closeImageModal(): void {
    this.isImageModalOpen = false;
    this.resetZoom();
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }
  }

  // Zoom functions
  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
      if (this.zoomLevel === this.minZoom) {
        this.translateX = 0;
        this.translateY = 0;
      }
    }
  }

  resetZoom(): void {
    this.zoomLevel = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isDragging = false;
  }

  getImageTransform(): string {
    return `scale(${this.zoomLevel}) translate(${this.translateX}px, ${this.translateY}px)`;
  }

  onImageMouseDown(event: MouseEvent): void {
    if (this.zoomLevel > 1) {
      this.isDragging = true;
      this.startX = event.clientX - this.translateX;
      this.startY = event.clientY - this.translateY;
      event.preventDefault();
    }
  }

  onImageMouseMove(event: MouseEvent): void {
    if (this.isDragging && this.zoomLevel > 1) {
      this.translateX = event.clientX - this.startX;
      this.translateY = event.clientY - this.startY;
      event.preventDefault();
    }
  }

  onImageMouseUp(): void {
    this.isDragging = false;
  }

  // Touch event handlers for mobile devices
  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      // Single touch - pan
      if (this.zoomLevel > 1) {
        this.touchStartX = event.touches[0].clientX - this.translateX;
        this.touchStartY = event.touches[0].clientY - this.translateY;
      } else {
        // Swipe to navigate
        this.touchStartX = event.touches[0].clientX;
      }
    } else if (event.touches.length === 2) {
      // Two fingers - pinch to zoom
      event.preventDefault();
      this.touchStartDistance = this.getTouchDistance(event.touches);
      this.initialZoom = this.zoomLevel;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 1) {
      if (this.zoomLevel > 1) {
        // Pan when zoomed
        this.translateX = event.touches[0].clientX - this.touchStartX;
        this.translateY = event.touches[0].clientY - this.touchStartY;
      }
    } else if (event.touches.length === 2) {
      // Pinch zoom
      event.preventDefault();
      const currentDistance = this.getTouchDistance(event.touches);
      const scale = currentDistance / this.touchStartDistance;
      this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.initialZoom * scale));
      
      if (this.zoomLevel === this.minZoom) {
        this.translateX = 0;
        this.translateY = 0;
      }
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (event.changedTouches.length === 1 && this.zoomLevel === 1) {
      // Swipe navigation
      const deltaX = event.changedTouches[0].clientX - this.touchStartX;
      const threshold = 50;
      
      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          this.previousImage();
        } else {
          this.nextImage();
        }
      }
    }
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  nextImage(): void {
    if (this.project) {
      this.selectedImageIndex = (this.selectedImageIndex + 1) % this.project.imagesGallery.length;
      this.resetZoom();
    }
  }

  previousImage(): void {
    if (this.project) {
      this.selectedImageIndex = this.selectedImageIndex === 0
        ? this.project.imagesGallery.length - 1
        : this.selectedImageIndex - 1;
      this.resetZoom();
    }
  }

  formatProjectName(title: string): string {
    return title
      .toLowerCase()
      .normalize('NFD')  
      .replace(/[\u0300-\u036f]/g, '')  
      .replace(/[^a-z0-9]+/g, '-')  
      .replace(/^-+|-+$/g, '');  
  }

  private getProjectImageUrl(): string {
    let src: string;
    if (this.project?.imageUrl) {
      src = this.project.imageUrl;
    } else if (this.project?.imagesGallery?.length) {
      src = this.project.imagesGallery[0];
    } else {
      src = '/assets/images/logos/logo-full.webp';
    }
    return this.toAbsoluteUrl(src);
  }

  setMetaTags(): void {
    if (!this.project) {
      return;
    }

    const metaTitle = `${this.project.title} | Proyectos de construcción | Paredes y Asociados`;
    const metaDescription = this.project.description || 'Proyecto de construcción realizado por Paredes y Asociados en México.';
    const image = this.getProjectImageUrl();
    const slug = this.formatProjectName(this.project.title);
    const url = `${this.siteUrl}/proyectos/${slug}/${this.project.id}`;

    this.titleService.setTitle(metaTitle);
    this.metaService.updateTag({ name: 'description', content: metaDescription });

    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
    this.metaService.updateTag({ name: 'description', content: metaDescription });
    this.setCanonicalUrl(url);

    this.metaService.updateTag({
      name: 'keywords',
      content: `Paredes y Asociados, Proyectos de construcción, ${this.project.businessArea}`
    });

    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:locale', content: 'es_MX' });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Paredes y Asociados' });

    this.metaService.updateTag({ property: 'og:title', content: metaTitle });
    this.metaService.updateTag({ property: 'og:description', content: metaDescription });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:image', content: image });
    this.metaService.updateTag({ property: 'og:image:alt', content: this.project.title });

    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: metaTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: metaDescription });
    this.metaService.updateTag({ name: 'twitter:url', content: url });
    this.metaService.updateTag({ name: 'twitter:image', content: image });
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private toAbsoluteUrl(path: string): string {
    if (!path) {
      return `${this.siteUrl}/assets/images/logos/logo-full.webp`;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return `${this.siteUrl}${path}`;
  }
}
